import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Stack, TextField, MenuItem, Switch, Button, Typography, Checkbox,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Human-readable title per level (mirrors the envelope naming from the API).
const LEVEL_TITLE = {
    practice: 'Practice',
    offering: 'Offering',
    'segment-master': 'Segment Master',
    segment: 'Segment',
};

// Which parent foreign-key each level writes back to the API (Constraint #5).
const LEVEL_PARENT_FK = {
    practice: null,
    offering: 'practice',
    'segment-master': 'offering',
    segment: 'segment_master',
};

/**
 * Reusable add/edit dialog for any node in the 4-level master-data hierarchy.
 * Mirrors AddPracticeForm.js conventions (Formik + Yup, label -> name.toUpperCase()).
 *
 * For the `offering` level the parent Practice is a settable Select (FR-012).
 * For `segment-master` / `segment` the parent is fixed and shown read-only; its id
 * travels through Formik values (seeded from initialValues) and is echoed in the payload.
 */
export default function MasterNodeDialog({
    open,
    mode,
    level,
    initialValues,
    parentOptions,
    parentLabel,
    offeringOptions = [],
    masterOptions = [],
    onSubmit,
    onClose,
}) {
    const parentFkField = LEVEL_PARENT_FK[level];

    // Multi-target "stamp across parents" mode is add-only for segment-master / segment.
    // Edit stays single-parent (FR-009).
    const isMultiTarget = mode === 'add' && (level === 'segment-master' || level === 'segment');
    const pluralKey = level === 'segment-master' ? 'offerings' : 'segment_masters';

    const shape = {
        label: Yup.string().required(`${LEVEL_TITLE[level]} name required`),
        is_active: Yup.boolean().required('Active status is required.'),
    };
    if (isMultiTarget) {
        // Multi-target: drop the per-parent Order rule, require ≥1 selected target.
        shape[pluralKey] = Yup.array().min(1, 'Select at least one target');
    } else {
        shape.order = Yup.string().required('Order required');
        // Offering's Practice MUST be settable/changeable (FR-012) -> validate it.
        if (level === 'offering') {
            shape.practice = Yup.string().required('Practice required');
        }
    }
    const Schema = Yup.object().shape(shape);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            label: initialValues?.label ?? '',
            order: initialValues?.order ?? '',
            is_active: initialValues?.is_active ?? true,
            // parent FK carried through for every non-practice level
            practice: initialValues?.practice ?? '',
            offering: initialValues?.offering ?? '',
            segment_master: initialValues?.segment_master ?? '',
            // multi-target parent id lists (add mode only)
            offerings: initialValues?.offerings ?? [],
            segment_masters: initialValues?.segment_masters ?? [],
        },
        validationSchema: Schema,
        onSubmit: (values) => {
            if (isMultiTarget) {
                onSubmit({
                    label: values.label,
                    name: (values.label || '').toUpperCase(),
                    is_active: values.is_active,
                    [pluralKey]: values[pluralKey],
                });
                return;
            }
            const payload = {
                label: values.label,
                name: (values.label || '').toUpperCase(),
                order: values.order,
                is_active: values.is_active,
            };
            if (parentFkField) {
                payload[parentFkField] = values[parentFkField];
            }
            onSubmit(payload);
        },
    });

    const {
        errors, touched, values, isSubmitting,
        handleSubmit, getFieldProps, setFieldValue,
    } = formik;

    // Options backing the multi-select depend on the level being stamped.
    const multiOptions = level === 'segment-master' ? offeringOptions : masterOptions;
    const renderSelectedLabels = (selected) => (selected || [])
        .map((id) => {
            const opt = multiOptions.find((o) => o.id === id);
            return opt ? opt.label : id;
        })
        .join(', ');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <DialogTitle>
                        {mode === 'edit' ? 'Update ' : 'Add '}{LEVEL_TITLE[level]}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            {/* Fixed parent (segment-master / segment) shown read-only — single-target only */}
                            {(level === 'segment-master' || level === 'segment') && !isMultiTarget && (
                                <TextField
                                    fullWidth
                                    label={level === 'segment-master' ? 'Offering' : 'Segment Master'}
                                    value={parentLabel || ''}
                                    InputProps={{ readOnly: true }}
                                    disabled
                                />
                            )}

                            {/* Multi-target: stamp copies across many parents (add mode, FR-001/FR-003) */}
                            {isMultiTarget && (
                                <TextField
                                    select
                                    fullWidth
                                    label={level === 'segment-master' ? 'Offerings' : 'Master (Offering)'}
                                    value={values[pluralKey]}
                                    onChange={(e) => setFieldValue(pluralKey, e.target.value)}
                                    error={Boolean(touched[pluralKey] && errors[pluralKey])}
                                    helperText={touched[pluralKey] && errors[pluralKey]}
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => renderSelectedLabels(selected),
                                    }}
                                >
                                    {level === 'segment-master'
                                        ? offeringOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                <Checkbox checked={values[pluralKey].includes(option.id)} />
                                                {option.label}
                                            </MenuItem>
                                        ))
                                        : masterOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                <Checkbox checked={values[pluralKey].includes(option.id)} />
                                                {`${option.label} (${option.offering_label})`}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            )}

                            {/* Settable parent Practice for the offering level (FR-012) */}
                            {level === 'offering' && (
                                <TextField
                                    select
                                    fullWidth
                                    label="Practice"
                                    value={values.practice}
                                    onChange={(event) => setFieldValue('practice', event.target.value)}
                                    error={Boolean(touched.practice && errors.practice)}
                                    helperText={touched.practice && errors.practice}
                                >
                                    {(parentOptions || []).map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <TextField
                                fullWidth
                                label={`${LEVEL_TITLE[level]} Name`}
                                {...getFieldProps('label')}
                                error={Boolean(touched.label && errors.label)}
                                helperText={touched.label && errors.label}
                            />

                            {!isMultiTarget && (
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Order"
                                    {...getFieldProps('order')}
                                    error={Boolean(touched.order && errors.order)}
                                    helperText={touched.order && errors.order}
                                />
                            )}

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography sx={{ color: '#637381' }}>Is Active?</Typography>
                                <Switch
                                    name="is_active"
                                    checked={Boolean(values.is_active)}
                                    onChange={(event, checked) => setFieldValue('is_active', checked)}
                                />
                                <ErrorMessage name="is_active">
                                    {(msg) => (
                                        <span style={{ color: '#FF4842', fontSize: '12px' }}>{msg}</span>
                                    )}
                                </ErrorMessage>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={onClose} color="inherit">Cancel</Button>
                        <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                            {mode === 'edit' ? 'Update' : 'Add'}
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
}

MasterNodeDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['add', 'edit']).isRequired,
    level: PropTypes.oneOf(['practice', 'offering', 'segment-master', 'segment']).isRequired,
    initialValues: PropTypes.object,
    parentOptions: PropTypes.array,
    parentLabel: PropTypes.string,
    offeringOptions: PropTypes.array,
    masterOptions: PropTypes.array,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
